import { Link, useParams } from 'react-router-dom'

import { BetsTable } from '../components/bets.tsx'
import { SignalCard } from '../components/signals.tsx'
import { Badge, Card, CardBody, CardHead, LoadError, OddsButton, PageHead, Skeleton } from '../components/ui.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { useApi } from '../lib/api.ts'
import { slipNames } from '../lib/betslip.tsx'
import { kickOffTime, toKickOff } from '../lib/format.ts'
import type { MarketDetailPayload } from '../../shared/types.ts'

/**
 * One market, cut to what a player needs: when it kicks off and whether it still takes
 * bets, its selections at bookie prices, and the player's own bets on it.
 */
export function MarketDetail() {
  const { marketId = '' } = useParams()
  const { data, error, loading, reload } = useApi<MarketDetailPayload>(`/markets/${marketId}`, 15_000)
  usePageTitle(data?.event ?? 'Market')

  if (error) return <main className="content"><LoadError error={error} onRetry={reload} /></main>
  if (loading || !data) {
    return (
      <main className="content">
        <Card>
          <CardBody>
            <Skeleton rows={6} />
          </CardBody>
        </Card>
      </main>
    )
  }

  const kick = toKickOff(data.startTime)
  const open = !data.closed && !data.suspended

  return (
    <main className="content">
      <PageHead
        breadcrumb={
          <>
            <Link to="/" className="u-faint">
              Today
            </Link>
            <span className="u-faint">/</span>
            {data.eventId ? (
              <Link to={`/games/${encodeURIComponent(data.eventId)}`} className="u-faint">
                {data.event}
              </Link>
            ) : (
              <span className="u-faint">{data.event}</span>
            )}
          </>
        }
        title={`${data.event} · ${data.marketType}`}
        actions={
          <Link className="btn primary" to="/bet-slip">
            Bet slip
          </Link>
        }
      />

      <div className="row wrap" style={{ marginTop: -10, marginBottom: 18 }}>
        {data.closed ? null : data.suspended ? (
          <Badge tone="warn" dot>
            Suspended for the moment
          </Badge>
        ) : (
          <Badge dot>
            Kick-off {kickOffTime(data.startTime)} · {kick.label}
          </Badge>
        )}
        {data.competition && <Badge>{data.competition}</Badge>}
        {data.score && <Badge outline>Score {data.score}</Badge>}
      </div>

      <div className="stack">
        {/* Once betting has closed the prices are history, and beside a bet taken at a
            different one they only mislead — so a closed market shows the bets alone. */}
        {!data.closed && (
          <Card>
            <CardHead
              title="Selections"
              sub={open ? 'Tap a price to add it to your bet slip' : 'Betting is suspended for the moment'}
            />
            <CardBody>
              <div className={`runner-grid${data.runners.length === 2 || data.runners.length === 3 ? ` n${data.runners.length}` : ''}`}>
                {data.runners.map((r) => (
                  <div className="runner-row" key={r.selectionId}>
                    <span className="runner-name">{r.name}</span>
                    <OddsButton
                      disabled={!open}
                      selection={{
                        marketId: data.marketId,
                        selectionId: r.selectionId,
                        event: data.event,
                        ...slipNames(r.name, data.marketType),
                        price: r.price,
                      }}
                    />
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {data.signals.length > 0 && (
          <Card>
            <CardHead title="Moving now" sub="Prices here that moved a long way in a short time" />
            <CardBody style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {data.signals.map((s) => (
                <SignalCard key={`${s.selectionId}:${s.kind}`} signal={s} showEvent={false} grow />
              ))}
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHead title="Your bets" sub="On this market, singles and accumulators" />
          <CardBody flush>
            {data.bets.length === 0 ? (
              <div style={{ padding: 18 }} className="u-faint">
                No bets on this market.
              </div>
            ) : (
              <BetsTable bets={data.bets} />
            )}
          </CardBody>
        </Card>
      </div>
    </main>
  )
}
